/* =========================================================================
   ClueVine — shareable result card
   Renders a finished puzzle to a branded PNG (the same "Midnight Garden" look
   as og.png) entirely client-side on a <canvas>, then either hands it to the
   native share sheet as a file or downloads it. The emoji grid is drawn as text
   so it stays mode-agnostic — whatever squares a mode puts in its shareGrid.
   ========================================================================= */

export interface ShareCardData {
  mode: string;
  tag: string; // "Daily #153" | "Endless"
  headline: string; // "Solved in 1!"
  grid: string[]; // emoji rows (may be empty)
  score: string; // "1,257"
  url: string; // "cluevine.com"
  won: boolean;
}

const C = {
  bg: '#0e1512',
  ink: '#e8efe7',
  inkSoft: '#a6b3a8',
  vine: '#5fb98c',
  vineBright: '#7fd0a4',
  berry: '#e07a9e',
};

const EMOJI = '"Segoe UI Emoji", "Apple Color Emoji", "Noto Color Emoji", sans-serif';

/** Draw the result card and return it as a PNG blob (or null if unsupported). */
export async function renderShareCard(d: ShareCardData): Promise<Blob | null> {
  const W = 1080;
  const H = 1350;
  const canvas = document.createElement('canvas');
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;

  // The app already requests the brand fonts; make sure they're ready to paint.
  try {
    await (document as Document & { fonts?: FontFaceSet }).fonts?.ready;
  } catch {
    /* fonts API unavailable — fall back to system fonts */
  }

  // Background + top halo (mirrors the OG card).
  ctx.fillStyle = C.bg;
  ctx.fillRect(0, 0, W, H);
  const halo = ctx.createRadialGradient(W / 2, 20, 40, W / 2, 20, W);
  halo.addColorStop(0, 'rgba(95, 185, 140, 0.20)');
  halo.addColorStop(1, 'rgba(95, 185, 140, 0)');
  ctx.fillStyle = halo;
  ctx.fillRect(0, 0, W, H);

  const cx = W / 2;
  const rows = d.grid;
  const cols = rows.length ? Math.max(...rows.map((r) => [...r].length)) : 0;
  const gridFont = rows.length
    ? Math.min(92, Math.floor(900 / cols), Math.floor(520 / (rows.length * 1.16)))
    : 0;
  const gridLH = gridFont * 1.16;
  const gridH = rows.length * gridLH;

  // Flowing vertical stack, centred between the header margin and the footer.
  const WM = 104;
  const TAG = 38;
  const HEAD = 76;
  const SCORE = 130;
  const G1 = 40;
  const G2 = 44;
  const G3 = 44;
  const G4 = 30;
  const total = WM + G1 + TAG + G2 + HEAD + G3 + (rows.length ? gridH + G4 : 0) + SCORE;
  const top = 150;
  const bottom = H - 150;
  let y = top + Math.max(0, (bottom - top - total) / 2);

  ctx.textBaseline = 'top';

  // Wordmark (two-tone, centred).
  ctx.font = `900 ${WM}px Fraunces, Georgia, serif`;
  const wClue = ctx.measureText('Clue').width;
  const wVine = ctx.measureText('Vine').width;
  const sx = cx - (wClue + wVine) / 2;
  ctx.textAlign = 'left';
  ctx.fillStyle = C.ink;
  ctx.fillText('Clue', sx, y);
  ctx.fillStyle = C.vineBright;
  ctx.fillText('Vine', sx + wClue, y);
  ctx.textAlign = 'center';
  y += WM + G1;

  // Mode · tag.
  ctx.font = `600 ${TAG}px "Hanken Grotesk", system-ui, sans-serif`;
  ctx.fillStyle = C.inkSoft;
  ctx.fillText(`${d.mode} · ${d.tag}`, cx, y);
  y += TAG + G2;

  // Headline.
  ctx.font = `600 ${HEAD}px Fraunces, Georgia, serif`;
  ctx.fillStyle = d.won ? C.ink : C.berry;
  ctx.fillText(d.headline, cx, y);
  y += HEAD + G3;

  // Emoji grid.
  if (rows.length) {
    ctx.font = `${gridFont}px ${EMOJI}`;
    const ls = ctx as CanvasRenderingContext2D & { letterSpacing?: string };
    const prev = ls.letterSpacing;
    try {
      ls.letterSpacing = `${Math.round(gridFont * 0.06)}px`;
    } catch {
      /* letterSpacing unsupported — squares just sit flush */
    }
    for (const row of rows) {
      ctx.fillText(row, cx, y);
      y += gridLH;
    }
    try {
      ls.letterSpacing = prev ?? '0px';
    } catch {
      /* ignore */
    }
    y += G4;
  }

  // Score.
  ctx.font = `500 ${SCORE}px "DM Mono", ui-monospace, monospace`;
  ctx.fillStyle = C.ink;
  ctx.fillText(d.score, cx, y);

  // Footer domain (anchored to the bottom).
  ctx.textBaseline = 'alphabetic';
  ctx.font = '500 36px "DM Mono", ui-monospace, monospace';
  ctx.fillStyle = C.vine;
  ctx.fillText(d.url, cx, H - 70);

  return await new Promise<Blob | null>((resolve) =>
    canvas.toBlob((b) => resolve(b), 'image/png'),
  );
}

export type ShareImageResult = 'shared' | 'downloaded' | 'failed';

/** Native-share the PNG as a file where supported, otherwise download it. */
export async function shareImageOrDownload(
  blob: Blob,
  filename: string,
  text: string,
): Promise<ShareImageResult> {
  try {
    const file = new File([blob], filename, { type: 'image/png' });
    const nav = navigator as Navigator & {
      canShare?: (data: { files?: File[] }) => boolean;
      share?: (data: { files?: File[]; text?: string }) => Promise<void>;
    };
    if (
      typeof nav.share === 'function' &&
      typeof nav.canShare === 'function' &&
      nav.canShare({ files: [file] })
    ) {
      try {
        await nav.share({ files: [file], text });
        return 'shared';
      } catch {
        // User dismissed the share sheet — treat as handled, don't also download.
        return 'shared';
      }
    }
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 2000);
    return 'downloaded';
  } catch {
    return 'failed';
  }
}
